import { t } from "../utils/i18n.ts";
import { COLORS } from "../core/constants.ts";
import { GAME, FINAL_WAVE } from "../core/config.ts";
import { ECHELON } from "../rendering/designTokens.ts";
import { createMascot } from "./OperatorMascot.ts";
import { UI_FONT_STACK, UI_Z, roleFontSize, withUiScale } from "./uiTokens.ts";
import { ctaSystemCss } from "./ctaStyles.ts";
import { DEFAULT_INITIALS } from "../core/initials.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";

export type GameOverCallback = () => void;

export interface RunStats {
  enemiesKilled: number;
  towersBuilt: number;
  goldEarned: number;
  goldSpent: number;
}

/**
 * Game Over overlay — "SYSTEM COMPROMISED" boot-style with
 * typewriter header, stat lines, rolling score counter, CRT distortion.
 * Layer 4 (HTML/CSS).
 */
export class GameOverScreen {
  private overlay: HTMLDivElement;

  constructor(parent: HTMLElement) {
    this.overlay = document.createElement("div");
    this.overlay.id = "game-over-screen";
    // BL-110 — modal dialog semantics for AT (the run has ended behind it); the
    // title (`#go-title`, set in show()) names the dialog.
    this.overlay.setAttribute("role", "dialog");
    this.overlay.setAttribute("aria-modal", "true");
    this.overlay.setAttribute("aria-labelledby", "go-title");
    this.injectStyles();
    parent.appendChild(this.overlay);
    this.overlay.style.display = "none";
  }

  show(
    score: number,
    wave: number,
    livesBonus: number,
    onRestart: GameOverCallback,
    onMainMenu: GameOverCallback,
    stats?: RunStats,
    isHighscore?: boolean,
    initials?: string,
  ): void;

  show(
    score: number,
    wave: number,
    onRestart: GameOverCallback,
    onMainMenu: GameOverCallback,
    stats?: RunStats,
    isHighscore?: boolean,
    initials?: string,
  ): void;

  show(
    score: number,
    wave: number,
    livesBonusOrOnRestart: number | GameOverCallback,
    onRestartOrOnMainMenu: GameOverCallback,
    onMainMenuOrStats?: GameOverCallback | RunStats,
    statsOrIsHighscore?: RunStats | boolean,
    isHighscoreOrInitials?: boolean | string,
    initialsMaybe?: string,
  ): void {
    const hasLivesBonus = typeof livesBonusOrOnRestart === "number";
    const livesBonus = hasLivesBonus ? livesBonusOrOnRestart : 0;
    const onRestart = hasLivesBonus
      ? onRestartOrOnMainMenu
      : (livesBonusOrOnRestart as GameOverCallback);
    const onMainMenu = hasLivesBonus
      ? (onMainMenuOrStats as GameOverCallback)
      : onRestartOrOnMainMenu;
    const stats = hasLivesBonus
      ? (statsOrIsHighscore as RunStats | undefined)
      : (onMainMenuOrStats as RunStats | undefined);
    const isHighscore = hasLivesBonus
      ? (isHighscoreOrInitials as boolean | undefined)
      : (statsOrIsHighscore as boolean | undefined);
    const initials = hasLivesBonus
      ? initialsMaybe
      : (isHighscoreOrInitials as string | undefined);

    this.overlay.innerHTML = "";
    this.overlay.style.display = "flex";
    this.overlay.classList.add("go-enter");

    // S126 — she had presence from Act II on; a breach after that means she
    // STAYS (Bible §3). An Act I defeat stays plain: FENRIR simply won.
    const echelonResident = wave > GAME.WAVES_ACT_I;

    // CRT crash overlay
    const crt = document.createElement("div");
    crt.className = "go-crt-crash";
    this.overlay.appendChild(crt);

    // Faded broken grid background
    const gridBg = document.createElement("div");
    gridBg.className = "go-broken-grid";
    this.overlay.appendChild(gridBg);

    if (echelonResident) {
      // Her afterglow — a STATIC magenta veil on the dead screen (no animation).
      const veil = document.createElement("div");
      veil.className = "go-echelon-veil";
      this.overlay.appendChild(veil);
    }

    // CR-6: broken-monitor glitch overlays — a static-noise burst layer and a
    // sync-tear bar (heavier than the boot ambient glitch: the system is DOWN).
    const glitchStatic = document.createElement("div");
    glitchStatic.className = "go-static";
    this.overlay.appendChild(glitchStatic);
    const glitchTear = document.createElement("div");
    glitchTear.className = "go-tear";
    this.overlay.appendChild(glitchTear);

    const content = document.createElement("div");
    content.className = "go-content";

    // S130+ (Story Bible §6.5 follow-up) — the fixed RESIDENT ending-comic
    // panel, Act II/III falls only (an Act I defeat is FENRIR's plain win, not
    // her occupation). Authored static asset (scripts/generate-ending-comics.mjs;
    // textless by contract — this DOM carries the words). onerror removal keeps
    // the screen whole until the curated image is committed.
    if (echelonResident) {
      const comic = document.createElement("img");
      comic.className = "go-comic";
      comic.src = "/img/endings/ending-resident.webp";
      comic.alt = t("gameover.comicAlt");
      comic.addEventListener("error", () => comic.remove());
      content.appendChild(comic);
    }

    // Typewriter header
    const title = document.createElement("h1");
    title.className = "go-title go-typewriter";
    title.id = "go-title";
    title.textContent = t("gameover.title");
    content.appendChild(title);

    const sub = document.createElement("div");
    sub.className = "go-sub";
    sub.textContent = t("gameover.subtitle");
    content.appendChild(sub);

    if (isHighscore) {
      const banner = document.createElement("div");
      banner.className = "gameover-highscore-banner";
      // Display safety depends on backend initials validation matching
      // INITIALS_CHARSET (no HTML-significant chars are allowed).
      banner.textContent = `★ NEW HIGHSCORE — ${initials ?? DEFAULT_INITIALS} ★`;
      content.appendChild(banner);
    }

    // Stat lines
    const statsEl = document.createElement("div");
    statsEl.className = "go-stats";
    const statLines = [
      { label: t("gameover.stat.wave"), value: String(wave) },
      {
        label: t("gameover.stat.kills"),
        value: String(stats?.enemiesKilled ?? 0),
      },
      {
        label: t("gameover.stat.towers"),
        value: String(stats?.towersBuilt ?? 0),
      },
      {
        label: t("gameover.stat.gold"),
        value: String(stats?.goldEarned ?? 0),
      },
    ];
    for (const s of statLines) {
      const row = document.createElement("div");
      row.className = "go-stat-row";
      row.innerHTML = `<span>${s.label}</span><span class="go-stat-val">${s.value}</span>`;
      statsEl.appendChild(row);
    }
    if (echelonResident) {
      const row = document.createElement("div");
      row.className = "go-stat-row go-stat-echelon";
      row.innerHTML = `<span>${t("gameover.stat.echelon")}</span><span class="go-echelon-resident">${t("gameover.stat.echelonResident")}</span>`;
      statsEl.appendChild(row);
    }
    content.appendChild(statsEl);

    // S130 (Story Bible §6.5): a fall in Act II/III is a logged stand — she
    // keeps the node, the Global Defense Log keeps you. Act I stays plain.
    if (echelonResident) {
      const epilogue = document.createElement("div");
      epilogue.className = "go-epilogue";
      epilogue.textContent = `${t("gameover.carrier")}  //  ${t("gameover.onRecord", { value: wave })}`;
      content.appendChild(epilogue);
    }

    const bonusLine = document.createElement("div");
    bonusLine.className = "go-score-bonus";
    bonusLine.innerHTML = `<span>${t("score.bonus.label")}</span><span class="go-stat-val">${t("score.bonus.value", { value: livesBonus })}</span>`;
    content.appendChild(bonusLine);

    // Rolling score counter
    const scoreLine = document.createElement("div");
    scoreLine.className = "go-final-score";
    scoreLine.textContent = t("gameover.score", { value: score });
    content.appendChild(scoreLine);
    this.animateScore(scoreLine, score);

    // WOW Pass 2 (item 5) — the run-end reveal loops back to the COMPETE
    // framing: the score is logged to the same GLOBAL DEFENSE LOG board the
    // start screen surfaces. Static line (no keyframes → reduced-motion-safe);
    // the count-up above is the motion beat. This is the seam where the
    // player's global rank (item 1, Game Center) plugs in once wired.
    const standing = document.createElement("div");
    standing.className = "go-standing";
    standing.textContent = t("score.logged");
    content.appendChild(standing);

    // Bug 16: past wave 35 means this run won the campaign then died in endless,
    // so FINAL SCORE is the WAVE-35 campaign result (the HIGHEST WAVE stat shows
    // the endless reach). Clarify it so the score doesn't read as the W36 death.
    if (wave > FINAL_WAVE) {
      const campaignNote = document.createElement("div");
      campaignNote.className = "go-campaign-note";
      campaignNote.textContent = t("gameover.campaignResult", {
        value: FINAL_WAVE,
      });
      content.appendChild(campaignNote);
    }

    // Buttons
    const btnRow = document.createElement("div");
    btnRow.className = "go-btn-row";

    // CR-31 (D1): two-button choice — RESTART RUN (true restart, same operator/draft)
    // vs MAIN MENU (back to the boot main menu). Previously a single button labeled REBOOT
    // emitted returnToMenu, which conflated the two intents.
    // BL-188 — ONE premium CTA language: RESTART is the single SENTINEL_GREEN
    // primary (the focused action — recovery is the dominant intent on defeat),
    // MAIN MENU the de-emphasised ghost secondary. Matches Victory's elevation.
    const restartBtn = document.createElement("button");
    restartBtn.className = "go-btn go-btn-primary go-btn-restart";
    restartBtn.innerHTML = `<span class="cta-foil"></span>${t("gameover.restart")}`;
    restartBtn.addEventListener("click", () => {
      this.hide();
      onRestart();
    });
    btnRow.appendChild(restartBtn);

    const menuBtn = document.createElement("button");
    menuBtn.className = "go-btn go-btn-menu";
    menuBtn.textContent = t("gameover.mainMenu");
    menuBtn.addEventListener("click", () => {
      this.hide();
      onMainMenu();
    });
    btnRow.appendChild(menuBtn);

    content.appendChild(btnRow);

    // Bug 15: defeated VERA (down) + smug FENRIR (cheer) flank the content in a
    // centered flow ROW [VERA | content | FENRIR] — bottom-aligned, decorative,
    // pointer-events:none. Flowing them in (vs absolute offsets) keeps them from
    // overlapping the stats/buttons when the endless-death content block is tall.
    const veraMascot = createMascot({
      character: "vera",
      pose: "down",
      size: 200,
      frame: false,
      label: `OPERATOR: ${initials ?? DEFAULT_INITIALS}`,
      className: "go-mascot go-mascot-vera",
    });
    const fenrirMascot = createMascot({
      character: "fenrir",
      heroArt: "fail", // S144/BL-135: gloating FENRIR hero — ECHELON resident
      size: 150,
      frame: false,
      label: "ORACLE: FENRIR",
      quip: "GRILLED YOU.",
      className: "go-mascot go-mascot-fenrir",
    });
    const row = document.createElement("div");
    row.className = "go-row";
    row.append(veraMascot, content, fenrirMascot);
    this.overlay.appendChild(row);

    // Scrolling error log (diegetic machine noise — untranslated by the same
    // convention as SystemLog's ambient lines)
    const errorLog = document.createElement("div");
    errorLog.className = "go-error-log";
    const errors = [
      "ERR 0x4F21: CORE INTEGRITY ZERO",
      "ERR 0x7A03: DEFENSE PROTOCOL FAILED",
      "ERR 0x1B44: MEMORY BANK CORRUPTED",
      "ERR 0x9E12: ALL NODES OFFLINE",
      "WARN: ATTEMPTING EMERGENCY REBOOT...",
    ];
    if (echelonResident) {
      // S126: the breach report names her.
      errors.splice(3, 0, "ERR 0xEC4E: FOREIGN PROCESS RESIDENT — ECHELON");
    }
    errorLog.textContent = errors.join("  //  ");
    this.overlay.appendChild(errorLog);

    // BL-110 — move focus into the dialog so keyboard/SR users land on the
    // primary action (RESTART) and the dialog title is announced.
    restartBtn.focus();
  }

  hide(): void {
    this.overlay.style.display = "none";
    this.overlay.classList.remove("go-enter");
  }

  private animateScore(el: HTMLElement, target: number): void {
    // Reduced-motion (MotionSettings) — land on the final value at once; the
    // count-up tick is a motion beat, so it must resolve to a static value.
    if (isReducedMotion()) {
      el.textContent = t("gameover.score", { value: target });
      return;
    }
    const duration = 1500;
    const start = performance.now();
    const tick = (now: number): void => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(target * eased);
      el.textContent = t("gameover.score", { value: current });
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  private injectStyles(): void {
    if (document.getElementById("game-over-styles")) return;
    const style = document.createElement("style");
    style.id = "game-over-styles";
    style.textContent = `
      #game-over-screen {
        position: absolute; inset: 0; display: flex;
        flex-direction: column; align-items: center; justify-content: center;
        background: rgba(8, 12, 24, 0.95); z-index: ${UI_Z.END_SCREEN};
        font-family: ${UI_FONT_STACK}; overflow: hidden;
      }
      .go-enter { animation: go-crt-in 0.6s ease-out, go-monitor-flicker 7s steps(1) infinite 0.6s; }
      @keyframes go-crt-in {
        0% { filter: brightness(4) saturate(0); transform: scaleY(0.01); }
        30% { filter: brightness(2) saturate(0.5); transform: scaleY(1.05); }
        100% { filter: brightness(1) saturate(1); transform: scaleY(1); }
      }
      .go-crt-crash {
        position: absolute; inset: 0; pointer-events: none;
        background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.15) 2px, rgba(0,0,0,0.15) 4px);
        z-index: 1;
      }
      .go-broken-grid {
        position: absolute; inset: 0; pointer-events: none; opacity: 0.04;
        background: repeating-linear-gradient(90deg, ${COLORS.ORACLE_RED} 0px, transparent 1px, transparent 48px),
                    repeating-linear-gradient(0deg, ${COLORS.ORACLE_RED} 0px, transparent 1px, transparent 48px);
        z-index: 0;
      }
      /* S126: her afterglow on the dead screen — STATIC by design (she stays). */
      .go-echelon-veil {
        position: absolute; inset: 0; pointer-events: none; z-index: 1;
        background: radial-gradient(ellipse at center, transparent 45%, ${ECHELON.SIGNAL}21 78%, ${ECHELON.DEEP}40 100%);
      }
      .go-stat-echelon span:first-child { color: ${ECHELON.SIGNAL}; opacity: 0.85; }
      .go-echelon-resident {
        color: ${ECHELON.SIGNAL}; font-weight: bold;
        text-shadow: 0 0 8px ${ECHELON.SIGNAL};
      }
      /* S130 — the RESIDENT epilogue + the on-the-record line (Bible §6.5). */
      .go-epilogue {
        color: ${ECHELON.SIGNAL};
        opacity: 0.85;
        font-size: ${roleFontSize("caption")};
        letter-spacing: 2px;
        margin: 10px 0 4px;
        text-shadow: 0 0 8px ${ECHELON.SIGNAL}66;
      }
      /* BL-122 — bounded column: this screen stacks the most content (comic +
         verdict + highscore + 5 stats + epilogue + bonus + score + campaign
         note + actions), so it is capped to the fixed 900px design stage
         (index.html DESIGN_H). 856px leaves room for the bottom error-log and
         the stage edges; the comic is the only element that shrinks if the
         stack would otherwise run past the band. */
      .go-content {
        position: relative; z-index: 2; text-align: center;
        display: flex; flex-direction: column; align-items: center;
        max-height: 856px; min-height: 0; box-sizing: border-box;
        /* BL-184 wrap guard on the bounded content column (comic/stats/lines). */
        overflow-wrap: break-word; word-break: normal;
      }
      /* S130+ — the RESIDENT ending panel (3:2 landscape, shipped at 2x);
         slimmer than the victory one — this screen stacks more content. It is
         the flexible block (flex: 0 1 auto + min-height: 0): under vertical
         pressure it shrinks first, keeping the stats/actions on-screen. */
      .go-comic {
        display: block;
        flex: 0 1 auto;
        min-height: 0;
        width: 320px;
        max-width: 100%;
        aspect-ratio: 3 / 2;
        margin: 0 auto 14px;
        border: 1px solid ${ECHELON.SIGNAL};
        box-shadow: 0 0 18px ${ECHELON.SIGNAL}59;
        object-fit: cover;
      }
      .go-title {
        font-size: ${roleFontSize("display")}; color: ${COLORS.ORACLE_RED};
        text-shadow: 0 0 20px ${COLORS.ORACLE_RED}, 0 0 40px ${COLORS.ORACLE_RED};
        letter-spacing: 8px; margin-bottom: 4px;
      }
      .go-typewriter {
        overflow: hidden; white-space: nowrap; border-right: 3px solid ${COLORS.ORACLE_RED};
        animation: typewriter 1.2s steps(20) 0.3s both, blink-caret 0.6s step-end infinite, go-rgb 3.4s steps(1) infinite 1.5s;
        width: 0; display: inline-block;
      }
      @keyframes typewriter { to { width: 100%; } }
      @keyframes blink-caret { 50% { border-color: transparent; } }
      .go-sub {
        font-size: ${roleFontSize("caption")}; color: ${COLORS.ORACLE_RED}; opacity: 0.6;
        letter-spacing: 4px; margin-bottom: 24px;
      }
      .gameover-highscore-banner {
        font-size: ${roleFontSize("heading")}; color: ${COLORS.AMBER_WARN};
        text-shadow: 0 0 16px ${COLORS.AMBER_WARN}, 0 0 32px ${COLORS.AMBER_WARN};
        letter-spacing: 6px; margin-bottom: 16px;
        animation: go-hs-pulse 1s ease-in-out infinite alternate;
      }
      @keyframes go-hs-pulse {
        0% { opacity: 0.7; text-shadow: 0 0 8px ${COLORS.AMBER_WARN}; }
        100% { opacity: 1; text-shadow: 0 0 20px ${COLORS.AMBER_WARN}, 0 0 40px ${COLORS.AMBER_WARN}; }
      }
      .go-stats { margin: 16px 0; }
      .go-stat-row {
        display: flex; justify-content: space-between; width: 280px; margin: 0 auto;
        padding: 4px 0; font-size: ${roleFontSize("label")}; color: ${COLORS.TEXT_PRIMARY};
        border-bottom: 1px solid rgba(136, 224, 208, 0.1); letter-spacing: 1px;
      }
      .go-score-bonus {
        display: flex; justify-content: space-between; width: 280px; margin: 0 auto 8px;
        padding: 4px 0; font-size: ${roleFontSize("caption")}; color: ${COLORS.AMBER_WARN};
        letter-spacing: 1px;
      }
      .go-stat-val { color: ${COLORS.SENTINEL_GREEN}; font-weight: bold; }
      .go-final-score {
        font-size: ${withUiScale(28)}; color: ${COLORS.AMBER_WARN};
        text-shadow: 0 0 12px ${COLORS.AMBER_WARN};
        margin: 20px 0 6px; letter-spacing: 4px;
        font-variant-numeric: tabular-nums;
      }
      /* WOW Pass 2 (item 5) — the compete-framing standing line under the
         score; static (reduced-motion-safe), premium SENTINEL_GREEN log echo. */
      .go-standing {
        font-size: ${roleFontSize("micro")}; color: ${COLORS.SENTINEL_GREEN};
        opacity: 0.82; letter-spacing: 3px; margin: 0 0 14px;
        text-shadow: 0 0 8px rgba(136, 224, 208, 0.4);
      }
      .go-btn-row { display: flex; gap: 16px; justify-content: center; align-items: center; margin-top: 20px; }
      /* BL-188 — the shared premium CTA material (primary = SENTINEL_GREEN
         RESTART; MAIN MENU inherits the ghost secondary rank). Replaces the two
         bare hover-invert boxes; the reduced-motion seam comes with it. */
      ${ctaSystemCss({
        scope: "#game-over-screen",
        btnClass: "go-btn",
        primaryClass: "go-btn-primary",
        primaryColour: COLORS.SENTINEL_GREEN,
      })}
      .go-error-log {
        position: absolute; bottom: 12px; left: 0; right: 0; z-index: 2;
        font-size: ${roleFontSize("micro")}; color: ${COLORS.ORACLE_RED}; opacity: 0.3;
        white-space: nowrap; overflow: hidden;
        animation: scroll-log 20s linear infinite;
      }
      @keyframes scroll-log { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
      /* Bug 15: mascots flow in a centered row beside the content (no overlap). */
      .go-row {
        position: relative; z-index: 2;
        display: flex; align-items: flex-end; justify-content: center;
        gap: 56px; max-width: 100%;
      }
      .go-mascot { flex: none; align-self: flex-end; z-index: 1; pointer-events: none; }
      .go-mascot-fenrir { opacity: 0.9; }
      /* (No viewport-size query: rendered inside the fixed 1600×900 stage and
         scaled to fit, so the mascot stays part of the composition everywhere.) */
      /* Bug 16: campaign (wave-35) result clarifier on an endless death. */
      .go-campaign-note {
        font-size: ${roleFontSize("micro")}; color: ${COLORS.SENTINEL_GREEN}; opacity: 0.85;
        letter-spacing: 3px; margin: -10px 0 6px;
        text-shadow: 0 0 8px rgba(136, 224, 208, 0.4);
      }

      /* ── CR-6: broken-monitor glitch (the mainframe is DOWN) ─────────────── */
      @keyframes go-monitor-flicker {
        0%,96%,100% { filter: none; }
        97% { filter: brightness(1.4) contrast(1.2); }
        98% { filter: brightness(0.7); }
        99% { filter: brightness(1.15); }
      }
      .go-content { animation: go-glitch-jitter 5.3s steps(1) infinite 1s; }
      @keyframes go-glitch-jitter {
        0%,92%,100% { transform: translate(0, 0); }
        93% { transform: translate(-2px, 1px); }
        94% { transform: translate(2px, -1px); }
        95% { transform: translate(-1px, 0); }
      }
      .go-static {
        position: absolute; inset: 0; z-index: 3; pointer-events: none; opacity: 0; mix-blend-mode: screen;
        background:
          repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0 1px, transparent 1px 2px),
          repeating-linear-gradient(90deg, rgba(255,45,85,0.05) 0 1px, transparent 1px 3px);
        background-size: 100% 2px, 4px 100%;
        animation: go-static-burst 4.3s steps(1) infinite;
      }
      @keyframes go-static-burst {
        0%,8%,46%,50%,100% { opacity: 0; }
        9% { opacity: 0.5; } 10% { opacity: 0.08; }
        47% { opacity: 0.7; } 49% { opacity: 0.12; }
      }
      .go-tear {
        position: absolute; left: 0; right: 0; height: 3px; top: -2%; z-index: 4; pointer-events: none;
        background: ${COLORS.ICE_CYAN}; opacity: 0; mix-blend-mode: screen;
        box-shadow: 0 0 8px ${COLORS.ICE_CYAN};
        animation: go-tear-sweep 6.5s steps(1) infinite;
      }
      @keyframes go-tear-sweep {
        0%,100% { opacity: 0; top: -2%; }
        12% { opacity: 0.85; top: 22%; } 13% { opacity: 0; }
        61% { opacity: 0.8; top: 64%; transform: scaleY(2); } 63% { opacity: 0; }
      }
      /* RGB-split flickers on the headline (text stays legible between bursts);
         appended to .go-typewriter's animation list so the type-on still runs. */
      @keyframes go-rgb {
        0%,90%,96%,100% { text-shadow: 0 0 20px ${COLORS.ORACLE_RED}, 0 0 40px ${COLORS.ORACLE_RED}; }
        92% { text-shadow: -3px 0 ${COLORS.ICE_CYAN}, 3px 0 ${COLORS.ORACLE_RED}, 0 0 20px ${COLORS.ORACLE_RED}; }
        94% { text-shadow: 3px 0 ${COLORS.ICE_CYAN}, -3px 0 ${COLORS.ORACLE_RED}, 0 0 20px ${COLORS.ORACLE_RED}; }
      }

      /* Reduced motion: calm the whole screen (glitch off, content steady). */
      @media (prefers-reduced-motion: reduce) {
        #game-over-screen, .go-enter,
        .go-content, .go-static, .go-tear, .go-title, .go-typewriter,
        .go-error-log, .gameover-highscore-banner {
          animation: none !important;
        }
        .go-static, .go-tear { opacity: 0 !important; }
        .go-typewriter { width: 100%; border-right: none; }
      }
    `;
    document.head.appendChild(style);
  }
}
